import React from 'react';
import { X, Lightning, Gauge, Tag, Robot, User, TextAlignLeft } from '@phosphor-icons/react';
import { getCategoryColor, getCategoryLabel } from 'config/ideaCategories';

export default function IdeaDetailPopup({ visible, setVisible, idea }) {
    if (!visible || !idea) return null;

    const handleClose = () => {
        setVisible(false);
    };

    const color = getCategoryColor(idea.category);
    const ease = idea.ease || (idea.difficulty ? 10 - idea.difficulty + 1 : 5);
    const isAIGenerated = idea.source !== 'manual';

    const getScoreLabel = (value) => {
        if (value <= 3) return 'Basso';
        if (value <= 6) return 'Medio';
        return 'Alto';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={handleClose}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b border-black/10">
                    <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                            />
                            <span className="text-xs font-medium text-black/50 uppercase tracking-wider">
                                {getCategoryLabel(idea.category)}
                            </span>
                        </div>
                        <h2 className="text-xl font-semibold text-black/90 leading-tight">
                            {idea.title}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors flex-shrink-0"
                    >
                        <X size={20} weight="bold" className="text-black/50" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Scores */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Impact */}
                        <div className="bg-black/[0.02] rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Lightning size={18} weight="fill" className="text-amber-500" />
                                <span className="text-sm font-medium text-black/70">
                                    Impatto Aziendale
                                </span>
                            </div>
                            <div className="flex items-end gap-2">
                                <span className="text-3xl font-bold text-black/90">
                                    {idea.impact}
                                </span>
                                <span className="text-sm text-black/50 pb-1">/10</span>
                            </div>
                            <div className="mt-2 h-2 bg-black/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-amber-500 rounded-full transition-all"
                                    style={{ width: `${idea.impact * 10}%` }}
                                />
                            </div>
                            <span className="text-xs text-black/50 mt-2 block">
                                {getScoreLabel(idea.impact)}
                            </span>
                        </div>

                        {/* Ease */}
                        <div className="bg-black/[0.02] rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Gauge size={18} weight="fill" className="text-emerald-500" />
                                <span className="text-sm font-medium text-black/70">
                                    Facilità d&apos;Uso
                                </span>
                            </div>
                            <div className="flex items-end gap-2">
                                <span className="text-3xl font-bold text-black/90">
                                    {ease}
                                </span>
                                <span className="text-sm text-black/50 pb-1">/10</span>
                            </div>
                            <div className="mt-2 h-2 bg-black/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-emerald-500 rounded-full transition-all"
                                    style={{ width: `${ease * 10}%` }}
                                />
                            </div>
                            <span className="text-xs text-black/50 mt-2 block">
                                {getScoreLabel(ease)}
                            </span>
                        </div>
                    </div>

                    {/* Description */}
                    {idea.description && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <TextAlignLeft size={18} className="text-black/40" />
                                <span className="text-sm font-medium text-black/70">
                                    Descrizione
                                </span>
                            </div>
                            <p className="text-black/70 text-sm leading-relaxed bg-black/[0.02] rounded-xl p-4">
                                {idea.description}
                            </p>
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-4 pt-2 border-t border-black/5">
                        <div className="flex items-center gap-2">
                            <Tag size={16} className="text-black/40" />
                            <span
                                className="text-xs font-medium px-2 py-1 rounded-full"
                                style={{
                                    backgroundColor: `${color}15`,
                                    color: color
                                }}
                            >
                                {getCategoryLabel(idea.category)}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {isAIGenerated ? (
                                <>
                                    <Robot size={16} className="text-purple-500" />
                                    <span className="text-xs text-black/50">
                                        Generata da AI
                                    </span>
                                </>
                            ) : (
                                <>
                                    <User size={16} className="text-blue-500" />
                                    <span className="text-xs text-black/50">
                                        Aggiunta manualmente
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-black/10 bg-black/[0.02]">
                    <button
                        onClick={handleClose}
                        className="w-full px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-black/90 transition-colors"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
}
